import * as React from "react";
import {
  LodgingForm,
  FormOptions,
  DateField,
  TravelersField,
  FormActionProvider,
  FormAction,
  FormError,
  TravelersFieldTriggerType,
  SubmitButton,
} from "@shared-ui/lodging-form";

import { useLocalization } from "@shared-ui/localization-context";
import { useDialog } from "@shared-ui/dialog-context";

import { UitkSpacing } from "uitk-react-spacing";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";

import { ComponentProps } from "typings/flexFramework/FlexComponent";
import { PropertyBexApiWrapper } from "components/shared/BexApiWrapper/PropertyBexApiWrapper";

import { useDateFieldData } from "./DateFieldData";
import { useTravelersFieldData } from "./TravelersFieldData";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { useRefContext } from "@shared-ui/ref-context";

const WrappedLodgingForm = PropertyBexApiWrapper(LodgingForm);

const formData: FormOptions["data"] = {
  id: "pdp-search-form",
  method: "GET",
  action: "/Hotel-Search",
};

interface ActionProps {
  onChange: (field: string) => void;
  onError: (errors: FormError[]) => void;
}

const ActionProvider: React.FC<ActionProps> = ({ onChange, onError, children }) => {
  const dispatch = React.useMemo(
    () => (action: FormAction) => {
      switch (action.type) {
        case "change":
          onChange(action.field);
          break;
        case "error":
          onError(action.errors);
      }
    },
    [onChange]
  );

  return <FormActionProvider value={dispatch}>{children}</FormActionProvider>;
};

export const MobileForm: React.FC<ComponentProps> = ({ context, children }) => {
  const dateFieldData = useDateFieldData();
  const travelersFieldData = useTravelersFieldData();

  const [, datePickerDialogActions] = useDialog("datepicker-start");

  const [rewriteHistory, setRewriteHistory] = React.useState(false);
  const openDatePickerFromTravelers = React.useRef(false);

  const { registerTarget } = useRefContext();

  // registering with the ref-context so the shared-ui sticky bar can find it.
  const formRef = registerTarget<HTMLFormElement>("pdp-search-form");
  const startDateRef = registerTarget<HTMLDivElement>("startDate");
  const endDateRef = React.useRef<HTMLElement | null>(null);

  const dateFieldProps = {
    fieldName: "dates",
    data: dateFieldData,
    startDateRef,
    endDateRef,
  };

  const travelersFieldProps = {
    fieldName: "travelers",
    data: travelersFieldData,
    closeOnSubmit: false,
    trigger: TravelersFieldTriggerType.INPUT,
  };

  const onChange = (field: string) => {
    if (field === "travelers") {
      datePickerDialogActions.openDialog();
      setRewriteHistory(true);
      openDatePickerFromTravelers.current = true;
      return;
    }

    // We cannot test the submission because it's not implemented by ReactDOM
    /* istanbul ignore next */
    if (field === "dates") {
      setTimeout(() => formRef?.current?.submit(), 1);
    }
  };

  const goBackInHistoryTwice = (): void => {
    history.go(-2);
  };

  // Go back twice in history if you're coming from the guest selector to avoid going
  // back to the guest selector when clicking the X button.
  const onDismiss = rewriteHistory ? goBackInHistoryTwice : undefined;

  // Go back twice in history if you're coming from the guest selector to avoid going
  // back to the guest selector when there is error in date picker.
  // Else go back in history once whenever there is an error in dialog to close the dialog.
  const onError = (): void => {
    if (openDatePickerFromTravelers.current) {
      goBackInHistoryTwice();
      openDatePickerFromTravelers.current = false;
    } else {
      history.go(-1);
    }
  };

  // This will reset the rewriteHistory state to false when the X button is clicked
  // and when a user goes back in the browser history.
  /* istanbul ignore next */
  const cleanupRewrite = (): void => {
    setRewriteHistory(false);
    window.removeEventListener("popstate", cleanupRewrite);
  };

  if (rewriteHistory) {
    window.addEventListener("popstate", cleanupRewrite);
  }

  return (
    <ActionProvider onChange={onChange} onError={onError}>
      <WrappedLodgingForm context={context} data={formData} formRef={formRef}>
        <UitkSpacing padding={{ blockend: "four" }}>
          <UitkLayoutFlex space="two" wrap="wrap">
            <UitkLayoutFlexItem grow={2}>
              <div>
                <DateField {...dateFieldProps} closeOnSubmit={false} onDismiss={onDismiss} isOpenOverride />
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem minWidth="third_width" grow={1}>
              <div>
                <TravelersField {...travelersFieldProps} />
              </div>
            </UitkLayoutFlexItem>
            <HiddenFields context={context} />
            {children}
          </UitkLayoutFlex>
        </UitkSpacing>
      </WrappedLodgingForm>
    </ActionProvider>
  );
};

export const DesktopForm: React.FC<ComponentProps> = ({ context, children }) => {
  const { formatText } = useLocalization();
  const dateFieldData = useDateFieldData();
  const travelersFieldData = useTravelersFieldData();
  const { registerTarget } = useRefContext();
  const startDateRef = registerTarget<HTMLDivElement>("startDate");
  const endDateRef = React.useRef<HTMLElement | null>(null);
  const dateFieldProps = {
    fieldName: "dates",
    data: dateFieldData,
    startDateRef,
    endDateRef,
  };

  const travelersFieldProps = {
    fieldName: "travelers",
    data: travelersFieldData,
    closeOnSubmit: true,
    trigger: TravelersFieldTriggerType.INPUT,
  };

  return (
    <WrappedLodgingForm context={context} data={formData}>
      <UitkSpacing padding={{ blockstart: "two", blockend: "four" }}>
        <UitkLayoutFlex space="two">
          <UitkLayoutFlexItem grow={2}>
            <div>
              <DateField {...dateFieldProps} />
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem minWidth="third_width">
            <div>
              <TravelersField {...travelersFieldProps} />
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem grow={1}>
            <div>
              <SubmitButton
                fieldName="submit"
                data={{
                  __typename: "LodgingFormSubmitButton",
                  label: formatText("hotels.checkAvailability"),
                }}
              />
            </div>
          </UitkLayoutFlexItem>
          <HiddenFields context={context} />
          {children}
        </UitkLayoutFlex>
      </UitkSpacing>
    </WrappedLodgingForm>
  );
};

export const SingleOfferForm: React.FC<ComponentProps> = ({ context, children }) => {
  const { formatText } = useLocalization();
  const dateFieldData = useDateFieldData();
  const travelersFieldData = useTravelersFieldData();
  const { registerTarget } = useRefContext();
  const startDateRef = registerTarget<HTMLDivElement>("startDate");
  const endDateRef = React.useRef<HTMLElement | null>(null);
  const dateFieldProps = {
    fieldName: "dates",
    data: dateFieldData,
    startDateRef,
    endDateRef,
  };

  const travelersFieldProps = {
    fieldName: "travelers",
    data: travelersFieldData,
    closeOnSubmit: true,
    trigger: TravelersFieldTriggerType.INPUT,
  };

  return (
    <WrappedLodgingForm context={context} data={formData}>
      <UitkLayoutFlex space="three" wrap="wrap">
        <UitkLayoutFlexItem minWidth="full_width">
          <div>
            <DateField {...dateFieldProps} />
          </div>
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem minWidth="full_width">
          <div>
            <TravelersField {...travelersFieldProps} />
          </div>
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem minWidth="full_width">
          <div>
            <SubmitButton
              fieldName="submit"
              data={{
                __typename: "LodgingFormSubmitButton",
                label: formatText("hotels.checkAvailability"),
              }}
            />
          </div>
        </UitkLayoutFlexItem>
        <HiddenFields context={context} />
        {children}
      </UitkLayoutFlex>
    </WrappedLodgingForm>
  );
};

const HiddenFields: React.FC<{ context: ExtendedContextStore }> = ({ context }) => {
  const selectedId = String(context.searchContext.location.id);
  const regionId = String(context.searchContext.location.parent?.id);

  return (
    <>
      <input type="hidden" name="selected" value={selectedId} />
      <input type="hidden" name="regionId" value={regionId} />
    </>
  );
};
